"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const Path_1 = __importDefault(require("transition-common/lib/services/path/Path"));
const PathLayerMapEvents_1 = require("./PathLayerMapEvents");
const layers_config_1 = require("../../../config/layers.config");
const MapCursorHelper_1 = require("../MapCursorHelper");
/* This file encapsulates map events specific for the paths */
const isAgenciesActiveSection = (activeSection) => activeSection === 'agencies';
// Flag to prevent concurrent path updates which would cause race conditions
// when determining waypoint insertion position
let isPathUpdateInProgress = false;
/**
 * Warns and returns true when the map is too far out to add a waypoint.
 * @param {number} zoom Current map zoom
 */
const rejectWaypointBelowMinZoom = (zoom) => {
    if (zoom >= layers_config_1.WAYPOINT_MIN_ZOOM) {
        return false;
    }
    ServiceLocator_1.default.eventManager.emit('warning', { name: 'WaypointMinZoom' });
    return true;
};
/**
 * Deduplicates an array of map features by their path ID property.
 * queryRenderedFeatures can return the same path multiple times when
 * its geometry spans multiple map tiles.
 * See official documentation for the queryRenderedFeatures method:
 * "Because features come from tiled vector data or GeoJSON data
 * that is converted to tiles internally, feature geometries may
 * be split or duplicated across tile boundaries and, as a result,
 * features may appear multiple times in query results."
 */
const deduplicatePathFeatures = (paths) => {
    const seen = new Set();
    return paths.filter((feature) => {
        var _a;
        const id = (_a = feature.properties) === null || _a === void 0 ? void 0 : _a.id;
        if (!id || seen.has(id))
            return false;
        seen.add(id);
        return true;
    });
};
const onPathWaypointMouseDown = (e) => {
    var _a, _b, _c;
    // start drag:
    const removingWaypoint = ServiceLocator_1.default.keyboardManager.keyIsPressed('alt');
    if (e.features && e.features[0] && !removingWaypoint) {
        const map = e.target;
        const feature = e.features[0];
        ServiceLocator_1.default.eventManager.emit('map.disableDragPan');
        map._currentDraggingFeature = 'waypoint';
        (0, MapCursorHelper_1.addDraggingClass)();
        // Create a clean GeoJSON object with only the needed properties to avoid
        // MapLibre serialization errors ("can't serialize object of unregistered class")
        const cleanFeature = {
            type: 'Feature',
            geometry: feature.geometry,
            properties: {
                afterNodeIndex: (_a = feature.properties) === null || _a === void 0 ? void 0 : _a.afterNodeIndex,
                waypointIndex: (_b = feature.properties) === null || _b === void 0 ? void 0 : _b.waypointIndex,
                type: (_c = feature.properties) === null || _c === void 0 ? void 0 : _c.type
            }
        };
        ServiceLocator_1.default.eventManager.emit('waypoint.startDrag', cleanFeature);
        e.originalEvent.stopPropagation();
    }
};
const onPathWaypointMouseUp = (e) => {
    var _a;
    const map = e.target;
    // stop drag if on edit node:
    if (map._currentDraggingFeature === 'waypoint') {
        // Clear any node hover state from dragging
        if (map._hoverNodeIntegerId && map._hoverNodeSource) {
            e.target.setFeatureState({ source: map._hoverNodeSource, id: map._hoverNodeIntegerId }, { size: 1, hover: false });
            map._hoverNodeIntegerId = null;
            map._hoverNodeId = null;
            map._hoverNodeSource = null;
        }
        const features = e.target.queryRenderedFeatures(e.point);
        const featureSources = features.map((feature) => feature.source);
        const hoverNodeIndex = featureSources.indexOf('transitNodes');
        if (hoverNodeIndex >= 0) {
            // replace waypoint by node
            const nodeGeojson = features[hoverNodeIndex];
            const path = ServiceLocator_1.default.selectedObjectsManager.getSingleSelection('path');
            const nodeIds = path.get('nodes');
            const hoveredNodeId = (_a = nodeGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
            if (!nodeIds.includes(hoveredNodeId)) {
                ServiceLocator_1.default.eventManager.emit('waypoint.replaceByNodeId', hoveredNodeId, ServiceLocator_1.default.keyboardManager.keyIsPressed('cmd') ? 'manual' : 'engine');
            }
            // TODO What about the else? if the waypoint is _on_ an existing node, should it be removed? Now it does nothing, no even update it, so there are 2 waypoints drawn on the map...
        }
        else {
            ServiceLocator_1.default.eventManager.emit('waypoint.update', e.lngLat.toArray());
        }
        map._currentDraggingFeature = null;
        (0, MapCursorHelper_1.removeDraggingClass)();
        (0, MapCursorHelper_1.removeHoverClass)(); // Clean up hover state since mouseleave doesn't fire during drag
        ServiceLocator_1.default.eventManager.emit('map.enableDragPan');
        e.originalEvent.stopPropagation();
    }
};
const onPathWaypointMouseMove = (e) => {
    var _a;
    const map = e.target;
    if (map._currentDraggingFeature === 'waypoint') {
        ServiceLocator_1.default.eventManager.emit('waypoint.drag', e.lngLat.toArray());
        // Check if dragging over a node to provide visual feedback
        // Here the user can insert a node at the waypoint pre-drag location:
        const nodeFeatures = e.target.queryRenderedFeatures(e.point, { layers: ['transitNodes'] });
        if (nodeFeatures.length > 0) {
            const nodeGeojson = nodeFeatures[0];
            const hoverNodeIntegerId = nodeGeojson.id;
            const hoverNodeId = (_a = nodeGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
            // Only update if hovering a different node
            if (map._hoverNodeIntegerId !== hoverNodeIntegerId) {
                // Unhover previous node if any
                if (map._hoverNodeIntegerId && map._hoverNodeSource && map._hoverNodeId) {
                    e.target.setFeatureState({ source: map._hoverNodeSource, id: map._hoverNodeIntegerId }, { size: 1, hover: false });
                }
                // Hover the new node
                e.target.setFeatureState({ source: nodeGeojson.source, id: hoverNodeIntegerId }, { size: 1.5, hover: true });
                map._hoverNodeIntegerId = hoverNodeIntegerId;
                map._hoverNodeId = hoverNodeId;
                map._hoverNodeSource = nodeGeojson.source;
            }
        }
        else if (map._hoverNodeIntegerId && map._hoverNodeSource) {
            // No longer over a node, clear the hover state
            e.target.setFeatureState({ source: map._hoverNodeSource, id: map._hoverNodeIntegerId }, { size: 1, hover: false });
            map._hoverNodeIntegerId = null;
            map._hoverNodeId = null;
            map._hoverNodeSource = null;
        }
        e.originalEvent.stopPropagation();
    }
};
const selectPath = (pathGeojson) => {
    var _a;
    const pathId = (_a = pathGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
    ServiceLocator_1.default.socketEventManager.emit('transitPath.read', pathId, null, (response) => {
        const transitPathEdit = new Path_1.default(Object.assign({}, response.path), false, ServiceLocator_1.default.collectionManager);
        const line = transitPathEdit.getLine();
        if (!line) {
            return;
        }
        const transitLine = line;
        transitLine.refreshSchedules(ServiceLocator_1.default.socketEventManager).then(() => {
            transitLine.startEditing();
            transitPathEdit.startEditing();
            ServiceLocator_1.default.eventManager.emit('map.disableBoxZoom');
            ServiceLocator_1.default.selectedObjectsManager.setSelection('path', [transitPathEdit]);
            ServiceLocator_1.default.selectedObjectsManager.setSelection('line', [line]);
            ServiceLocator_1.default.eventManager.emit('selected.updateLayers.path');
        });
    });
};
const hoverPath = (pathGeojson, map) => {
    var _a;
    if (map._hoverPathIntegerId && map._hoverPathId && map._hoverPathSource) {
        (0, PathLayerMapEvents_1.unhoverPath)(map._hoverPathId);
        map.setFeatureState({ source: map._hoverPathSource, id: map._hoverPathIntegerId }, { size: 2, hover: false });
    }
    map.setFeatureState({ source: pathGeojson.source, id: pathGeojson.id }, { size: 4, hover: true });
    // See https://github.com/alex3165/react-mapbox-gl/issues/506
    map._hoverPathIntegerId = pathGeojson.id;
    map._hoverPathId = (_a = pathGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
    map._hoverPathSource = pathGeojson.source;
};
/**
 * Shows a context menu for selecting one of multiple overlapping paths.
 * @param paths - Array of path GeoJSON features to show in the menu
 * @param e - The mouse event that triggered the menu
 */
const showPathSelectionContextMenu = (paths, e) => {
    const menu = [];
    paths.forEach((pathGeojson) => {
        var _a;
        const pathById = ServiceLocator_1.default.collectionManager.get('paths').getById((_a = pathGeojson.properties) === null || _a === void 0 ? void 0 : _a.id);
        if (!pathById)
            return;
        const pathObj = new Path_1.default(pathById.properties, false, ServiceLocator_1.default.collectionManager);
        const line = pathObj.getLine();
        menu.push({
            key: pathObj.getId(),
            title: `${line.toString(false)} • ${pathObj.toString(false)}`,
            onClick: () => selectPath(pathGeojson),
            onHover: () => hoverPath(pathGeojson, e.target)
        });
    });
    ServiceLocator_1.default.eventManager.emit('map.showContextMenu', e, menu);
};
// TODO Should we split this in individual functions with conditions instead?
// Test the performances first
// TODO Original code in click.events.js had a _draggingEventsOrder check. Is
// it still needed? If we have problems, there should be an event handler of
// higher priority to check it before running any other
// Click tolerance in pixels for path/node/waypoint detection
const CLICK_TOLERANCE = 5;
const onPathSectionMapClick = (e) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const features = e.target.queryRenderedFeatures([
        [e.point.x - CLICK_TOLERANCE, e.point.y - CLICK_TOLERANCE],
        [e.point.x + CLICK_TOLERANCE, e.point.y + CLICK_TOLERANCE]
    ]);
    const map = e.target;
    const featureSources = features.map((feature) => {
        return feature.source;
    });
    const selectedPath = ServiceLocator_1.default.selectedObjectsManager.getSingleSelection('path');
    const selectedLine = ServiceLocator_1.default.selectedObjectsManager.getSingleSelection('line');
    const clickedNodeIndex = featureSources.indexOf('transitNodes');
    const clickedWaypointIndex = featureSources.indexOf('transitPathWaypoints');
    const clickedSelectedNodeIndex = featureSources.indexOf('transitNodesSelected');
    const clickedSelectedPathIndex = featureSources.indexOf('transitPathsSelected');
    const clickedPathIndex = featureSources.indexOf('transitPaths');
    // Check if the selected path is among the clicked features
    // This is used to determine if we should insert waypoint at nearest segment vs at end
    // The selected path is rendered on transitPathsSelected layer, not transitPaths,
    // so we need to check both layers.
    const selectedPathHasBeenClicked = selectedPath !== undefined && clickedSelectedPathIndex >= 0;
    ServiceLocator_1.default.eventManager.emit('map.hideContextMenu');
    const path = selectedPath ? selectedPath : undefined;
    // Check if the selected path has unsaved changes
    const hasUnsavedChanges = path && (path.hasChanged() || (selectedLine && selectedLine.hasChanged()));
    // Check if clicking on a different (non-selected) path - handle before waypoint logic
    // Only allow path switching when:
    // - There are NO unsaved changes
    // - The selected path was NOT clicked (if multiple paths overlap and one is selected,
    //   we want to add a waypoint to the selected path, not switch)
    // When there are unsaved changes or the selected path was clicked, we skip this block
    // and let the click fall through to the waypoint addition logic.
    if (path &&
        !hasUnsavedChanges &&
        !selectedPathHasBeenClicked &&
        clickedPathIndex >= 0 &&
        clickedWaypointIndex < 0 &&
        clickedNodeIndex < 0 &&
        clickedSelectedNodeIndex < 0) {
        // Clicked on a different path (selected path was not among the clicked features)
        const paths = deduplicatePathFeatures(features.filter((feature) => feature.source === 'transitPaths'));
        // No changes - stop editing and proceed
        path.stopEditing();
        if (selectedLine) {
            selectedLine.stopEditing();
        }
        // Deselect triggers cleanup of map layers (transitNodesSelected, etc.)
        ServiceLocator_1.default.selectedObjectsManager.deselect('path');
        ServiceLocator_1.default.selectedObjectsManager.deselect('line');
        if (paths.length === 1) {
            selectPath(paths[0]);
        }
        else {
            // Multiple overlapping paths - show context menu
            showPathSelectionContextMenu(paths, e);
        }
        e.originalEvent.stopPropagation();
        return;
    }
    if (path && !path.isFrozen()) {
        e.originalEvent.stopPropagation();
        if (
        // clicked on waypoint (remove)
        clickedWaypointIndex >= 0 &&
            clickedNodeIndex < 0
        //&& serviceLocator.keyboardManager.keyIsPressed('alt')
        ) {
            // Skip if another path update is in progress to prevent race conditions
            if (isPathUpdateInProgress) {
                return;
            }
            isPathUpdateInProgress = true;
            const attributes = features[clickedWaypointIndex].properties || {};
            const path = selectedPath;
            try {
                yield path.removeWaypoint(attributes.afterNodeIndex, attributes.waypointIndex);
                ServiceLocator_1.default.selectedObjectsManager.setSelection('path', [path]);
                ServiceLocator_1.default.eventManager.emit('selected.updateLayers.path');
            }
            finally {
                isPathUpdateInProgress = false;
            }
        }
        else if (
        // insert waypoint in path at click on selected path
        clickedWaypointIndex < 0 &&
            clickedSelectedPathIndex >= 0 &&
            clickedNodeIndex < 0) {
            if (!path.allowsWaypoints() || rejectWaypointBelowMinZoom(map.getZoom())) {
                return;
            }
            // Skip if another path update is in progress to prevent race conditions
            if (isPathUpdateInProgress) {
                return;
            }
            isPathUpdateInProgress = true;
            const waypointType = path.attributes.data.temporaryManualRouting
                ? 'manual'
                : path.getData('routingEngine', 'engine');
            // Clicking on the selected path - let insertWaypoint find the correct segment
            try {
                yield path.insertWaypoint(e.lngLat.toArray(), waypointType, undefined, undefined);
                path.validate();
                ServiceLocator_1.default.selectedObjectsManager.setSelection('path', [path]);
                ServiceLocator_1.default.eventManager.emit('selected.updateLayers.path');
            }
            finally {
                isPathUpdateInProgress = false;
            }
        }
        else if (
        // add or remove node or add waypoint to path
        clickedWaypointIndex < 0) {
            // TODO Should not be determined here, the default value should be provided by the functions themselves
            const waypointType = path.attributes.data.temporaryManualRouting
                ? 'manual'
                : path.attributes.data.routingEngine || 'engine';
            let insertOrRemoveNodePromise = undefined;
            if (clickedNodeIndex >= 0 || clickedSelectedNodeIndex >= 0) {
                // add node
                // TODO Is it possible to have the selectedNode layer on at this time?
                const nodeGeojson = features[clickedNodeIndex] || features[clickedSelectedNodeIndex];
                const nodeId = (_a = nodeGeojson.properties) === null || _a === void 0 ? void 0 : _a.id;
                if (nodeId && e.originalEvent.shiftKey) {
                    insertOrRemoveNodePromise = path.insertNodeId(nodeId, 0, waypointType);
                }
                else if (nodeId && e.originalEvent.altKey) {
                    insertOrRemoveNodePromise = path.removeNodeId(nodeId);
                }
                else if (nodeId) {
                    insertOrRemoveNodePromise = path.insertNodeId(nodeId, null, waypointType);
                }
            }
            else if (!path.allowsWaypoints()) {
                return;
            }
            else {
                // add waypoint
                if (rejectWaypointBelowMinZoom(map.getZoom())) {
                    return;
                }
                // Skip if another path update is in progress to prevent race conditions
                if (isPathUpdateInProgress) {
                    return;
                }
                const lastNodeIndex = path.attributes.nodes.length - 1;
                if (lastNodeIndex < 0) {
                    return;
                }
                // If clicked on the selected path (or on the transitPathsSelected layer),
                // insert waypoint at the nearest segment.
                // If clicked elsewhere (including on other paths), add waypoint to the end.
                const clickedOnSelectedPath = selectedPathHasBeenClicked || clickedSelectedPathIndex >= 0;
                // Set flag before starting async operation to prevent race conditions
                isPathUpdateInProgress = true;
                insertOrRemoveNodePromise = path.insertWaypoint(e.lngLat.toArray(), waypointType, clickedOnSelectedPath ? undefined : lastNodeIndex, undefined);
            }
            if (insertOrRemoveNodePromise) {
                // Flag may already be set from waypoint insertion above; ensure it's set for node operations too
                isPathUpdateInProgress = true;
                try {
                    const response = yield insertOrRemoveNodePromise;
                    if (response.path) {
                        ServiceLocator_1.default.selectedObjectsManager.setSelection('path', [response.path]);
                        ServiceLocator_1.default.eventManager.emit('selected.updateLayers.path');
                    }
                    else {
                        console.error('error', response.error); // todo: better error handling
                    }
                }
                catch (error) {
                    console.error('error', error); // todo: better error handling
                }
                finally {
                    isPathUpdateInProgress = false;
                }
            }
        }
        else if (
        // add waypoint to selected path or insert node
        clickedNodeIndex < 0 &&
            clickedSelectedPathIndex >= 0 &&
            path.allowsWaypoints()) {
            // TODO Can this be part of the previous if? And not use an emit, but call a path function?
            (0, MapCursorHelper_1.addDraggingClass)();
            ServiceLocator_1.default.eventManager.emit('waypoint.insert', e.lngLat.toArray());
        }
    }
    else if (
    // select unique path, or show all paths
    (!path || (!path.hasChanged() && !selectedLine.hasChanged())) &&
        clickedPathIndex >= 0) {
        if (!selectedLine ||
            (path && !path.hasChanged() && !selectedLine.hasChanged()) ||
            (selectedLine && !path && !selectedLine.hasChanged())) {
            const paths = deduplicatePathFeatures(features.filter((feature) => feature.source === 'transitPaths'));
            if (paths.length === 1) {
                selectPath(paths[0]);
            }
            else {
                showPathSelectionContextMenu(paths, e);
            }
            e.originalEvent.stopPropagation();
        }
    }
});
const pathSectionEventDescriptors = [
    { type: 'map', eventName: 'click', condition: isAgenciesActiveSection, handler: onPathSectionMapClick },
    {
        type: 'layer',
        eventName: 'mousedown',
        layerName: 'transitPathWaypoints',
        condition: isAgenciesActiveSection,
        handler: onPathWaypointMouseDown
    },
    { type: 'map', eventName: 'mouseup', condition: isAgenciesActiveSection, handler: onPathWaypointMouseUp },
    { type: 'map', eventName: 'mousemove', condition: isAgenciesActiveSection, handler: onPathWaypointMouseMove }
];
exports.default = pathSectionEventDescriptors;
//# sourceMappingURL=PathSectionMapEvents.js.map